import React, { Fragment, forwardRef, type PropsWithChildren, type Ref } from 'react';
import { EngagementSpotlight } from '@atlassiansox/engagekit-ts/ui/Spotlight';
import { SpotlightManager } from '@atlassiansox/engagekit-ts/spotlight-manager';
import { SpotlightTarget } from '@atlassiansox/engagekit-ts/spotlight-target';

import { componentWithFG } from '@jira/platform__utils-feature-gates-gate-component/src/index.tsx';
import DeprecatedSpotlightTarget from '@atlaskit/onboarding/spotlight-target'; // eslint-disable-line @atlaskit/design-system/use-spotlight-package
import { AppSwitcher as AkAppSwitcherNav4 } from '@atlaskit/navigation-system/top-nav-items';
import { SwitchToTooltipText } from '@atlassian/switcher/switch-to-tooltip-text';
import { fg } from '@atlassian/jira-feature-gating';

type Props = {
	onOpen?(): void;
	onMouseEnter?(): void;
};

/**
 * AppSwitcherPlaceholderButton is designed to only show until the user has clicked it.
 * Then, it should hide itself forever.
 *
 * Motivation: This button acts as a placeholder for when the actual AppSwitcher
 * is loaded. We don't want to load the actual AppSwitcher until it's needed,
 * as it is large. So, the parent component shows this component until such time
 * as the user clicks on it. Then, the real AppSwitcher component is loaded, and
 * replaces this one.
 */
export const AppSwitcherPlaceholderButton: React.ForwardRefExoticComponent<
	Props & React.RefAttributes<HTMLButtonElement>
> = forwardRef(AppSwitcherPlaceholderButtonWithRef);

function AppSwitcherPlaceholderButtonWithRef(
	{ onOpen, onMouseEnter }: Props,
	ref: Ref<HTMLButtonElement>,
) {
	return (
		<AppSwitcherLightWrapper>
			<AkAppSwitcherNav4
				ref={ref}
				onClick={onOpen}
				onMouseEnter={onMouseEnter}
				label={SwitchToTooltipText}
				aria-expanded={false}
				aria-haspopup
				interactionName="jira_app_switcher"
			/>
		</AppSwitcherLightWrapper>
	);
}

const AppSwitcherLightWrapper = componentWithFG(
	'app_switcher_spotlight_gate',
	SpotLightWrapper,
	Fragment,
);

function SpotLightWrapper(props: PropsWithChildren) {
	return (
		<SpotlightManager>
			<div id="app-switcher-confluence-ap-ads-spotlight">
				{fg('twcg-2261-ap-spotlight-migration') ? (
					<SpotlightTarget name="app-switcher-spotlight">{props.children}</SpotlightTarget>
				) : (
					<DeprecatedSpotlightTarget name="app-switcher-spotlight">
						{props.children}
					</DeprecatedSpotlightTarget>
				)}
			</div>
			<EngagementSpotlight isMigrated engagementId="app-switcher-spotlight" />
		</SpotlightManager>
	);
}
